import { useState } from 'react';
import { exportToCSV } from '@/utils/export';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Download, Search, Trophy } from 'lucide-react';
import { rewardsService } from '@/services/rewards.service';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface ParticipationsTableProps {
  rewardId: string;
  rewardTitle: string;
}

export function ParticipationsTable({
  rewardId,
  rewardTitle,
}: ParticipationsTableProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  const queryParams = {
    maxResultCount: itemsPerPage,
    skipCount: (currentPage - 1) * itemsPerPage,
    ...(searchTerm && { filter: searchTerm }),
  };

  const { data, isLoading } = useQuery({
    queryKey: ['reward-participations', rewardId, queryParams],
    queryFn: () => rewardsService.getParticipations(rewardId, queryParams),
  });

  const handleExport = async () => {
    try {
      const allData = await rewardsService.getParticipations(rewardId, {
        maxResultCount: 10000,
        skipCount: 0,
        ...(searchTerm && { filter: searchTerm }),
      });

      const exportData = allData.items.map((participation) => ({
        User: participation.userName,
        Email: participation.userEmail,
        'Entry Date': format(
          new Date(participation.participationDate),
          'yyyy-MM-dd HH:mm:ss',
        ),
        'Tokens Bid': participation.tokensBid,
        'Lottery Number': participation.lotteryNumber || 'N/A',
        'Is Winner': participation.isWinner ? 'Yes' : 'No',
      }));

      const columns = [
        { key: 'User' as const, label: 'User' },
        { key: 'Email' as const, label: 'Email' },
        { key: 'Entry Date' as const, label: 'Entry Date' },
        { key: 'Tokens Bid' as const, label: 'Tokens Bid' },
        { key: 'Lottery Number' as const, label: 'Lottery Number' },
        { key: 'Is Winner' as const, label: 'Is Winner' },
      ];

      exportToCSV(exportData, columns, `${rewardTitle}-participants`);
    } catch (error) {
      console.error('Failed to export participations:', error);
    }
  };

  const totalPages = data ? Math.ceil(data.totalCount / itemsPerPage) : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Participants: {rewardTitle}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by user name or email..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="pl-8"
              />
            </div>
            <Button onClick={handleExport} variant="outline" size="sm">
              <Download className="mr-2 h-4 w-4" />
              Export CSV
            </Button>
          </div>

          {/* Stats */}
          {data && (
            <div className="flex gap-4 text-sm text-muted-foreground">
              <span>Total Participants: {data.totalCount}</span>
              <span>
                Showing {(currentPage - 1) * itemsPerPage + 1}-
                {Math.min(currentPage * itemsPerPage, data.totalCount)} of{' '}
                {data.totalCount}
              </span>
            </div>
          )}

          {/* Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Entry Date</TableHead>
                  <TableHead className="text-right">Tokens Bid</TableHead>
                  <TableHead className="text-right">Lottery Number</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center">
                      Loading participants...
                    </TableCell>
                  </TableRow>
                ) : !data?.items.length ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center">
                      No participants found
                    </TableCell>
                  </TableRow>
                ) : (
                  data.items.map((participation) => (
                    <TableRow key={participation.id}>
                      <TableCell className="font-medium">
                        {participation.userName}
                      </TableCell>
                      <TableCell>{participation.userEmail}</TableCell>
                      <TableCell>
                        {format(
                          new Date(participation.participationDate),
                          'MMM dd, yyyy HH:mm',
                        )}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {participation.tokensBid.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right">
                        {participation.lotteryNumber ? (
                          <Badge variant="outline">
                            {participation.lotteryNumber}
                          </Badge>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        {participation.isWinner ? (
                          <Badge
                            variant="success"
                            className="flex items-center gap-1 w-fit"
                          >
                            <Trophy className="h-3 w-3" />
                            Winner
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
